import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  FormHelperText,
  Grid,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { globalVariables } from "../../global/GlobalVariables";
import GenericPicker from "../../components/utilities/GenericPicker";
import {
  getEnvironmentVariable,
  isBrand
} from "../../global/environmentVariables";
import {
  getAvailableCurrencies,
  getTicketFees,
  getTicketingFeeSummary,
  numberWithCommas
} from "../../components/utilities/UtilityFunctions";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import { BCPCircleCheck } from "../../components/icons/bcp/BCPIcons";
const IS_BCP = isBrand("bcp");

export default function Pricing() {
  const { t } = useTranslation("translation", { keyPrefix: "pages.pricing" });
  const globalVars = globalVariables();

  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("USD");
  const [feeStructure, setFeeStructure] = useState(null);

  const [pricing, setPricing] = useState({
    price: "",
    sold: "",
    fees: "",
    amount: "-",
    total: "-",

    absorbFees: true
  });

  const currencies = getAvailableCurrencies();

  const handleChange = (e) => {
    if (
      e?.target?.name &&
      e?.target?.name !== "absorbFees" &&
      pricing[e?.target?.name] === e?.target?.value
    ) {
      return;
    }
    var newPricing = { ...pricing };
    if (e?.target?.name) {
      if (e?.target?.name === "absorbFees") {
        newPricing.absorbFees = !newPricing.absorbFees;
      } else if (
        !newPricing[e?.target?.name] ||
        newPricing[e?.target?.name] !== e?.target?.value
      ) {
        newPricing[e.target.name] = e?.target?.value;
      }
    }

    if (newPricing.price !== "" && newPricing.sold !== "") {
      let ticketingFeeSummary = getTicketingFeeSummary(
        newPricing.sold,
        newPricing.price,
        !newPricing.absorbFees && feeStructure?.FixedPlayer !== undefined
          ? feeStructure?.FixedPlayer
          : feeStructure?.Fixed,
        !newPricing.absorbFees && feeStructure?.PercentPlayer !== undefined
          ? feeStructure?.PercentPlayer
          : feeStructure?.Percent,
        newPricing.absorbFees
      );

      newPricing.fees = numberWithCommas(ticketingFeeSummary.fee?.toFixed(2));
      newPricing.amount = numberWithCommas(
        ticketingFeeSummary.amount?.toFixed(2)
      );
      newPricing.total = numberWithCommas(
        (
          ticketingFeeSummary.total?.toFixed(2) -
          (newPricing.absorbFees
            ? ticketingFeeSummary.fee?.toFixed(2) * newPricing.sold
            : 0)
        ).toFixed(2)
      );
    } else {
      newPricing.fees = "";
      newPricing.amount = "-";
      newPricing.total = "-";
    }
    setPricing(newPricing);
  };

  const getFeeStructure = async (_currency) => {
    setLoading(true);
    try {
      const _feeStructure = await getTicketFees({ currency: _currency });
      setFeeStructure(_feeStructure);
    } catch (err) {
      postNotification("error", err);
    }
    setLoading(false);
  };
  useEffect(() => {
    getFeeStructure(currency);
    // eslint-disable-next-line
  }, [currency]);
  useEffect(() => {
    handleChange();
    // eslint-disable-next-line
  }, [feeStructure]);
  return (
    <Box sx={{ width: "100%" }}>
      {loading && <ModalLoadingFull />}
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2 }}>
          <Grid
            item
            xs={12}
            sx={{
              p: 2
            }}
          >
            <Typography variant="h3" gutterBottom>
              {t("title")}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              p: 2
            }}
          >
            {IS_BCP ? (
              <Typography variant="body1" paragraph>
                {globalVars?.site?.title} {t("bcpDescription")}
              </Typography>
            ) : (
              <Typography variant="body1" paragraph>
                {globalVars?.site?.title} {t("partnerDescription")}
              </Typography>
            )}
            <Typography variant="body1" paragraph>
              {t("stripeDescription")}{" "}
              <Link
                href="https://stripe.com/"
                target="_Blank"
                underline="none"
                sx={{ color: "link.stripe" }}
              >
                Stripe
              </Link>{" "}
              {t("paymentPlatform")}{" "}
              {getEnvironmentVariable("APP_NAME") || "BCP"}{" "}
              {t("accountDetails")}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <GenericPicker
                  label={t("currency")}
                  required
                  inclAny={false}
                  name="currency"
                  list={currencies}
                  value={currency}
                  handleChange={(e) => {
                    setCurrency(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="price"
                  value={pricing.price}
                  label={t("ticketPrice")}
                  variant="outlined"
                  fullWidth
                  onBlur={handleChange}
                  onChange={handleChange}
                  type="number"
                  inputProps={{
                    min: 0
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="sold"
                  value={pricing.sold}
                  label={t("ticketsSold")}
                  variant="outlined"
                  fullWidth
                  onBlur={handleChange}
                  onChange={handleChange}
                  type="number"
                  inputProps={{
                    min: 0
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <ButtonGroup fullWidth>
                  <Button
                    variant={!pricing.absorbFees ? "outlined" : "contained"}
                    onClick={() => {
                      handleChange({
                        target: {
                          name: "absorbFees"
                        }
                      });
                    }}
                    startIcon={pricing.absorbFees && <BCPCircleCheck />}
                  >
                    {t("organizer")}
                  </Button>
                  <Button
                    variant={!pricing.absorbFees ? "contained" : "outlined"}
                    onClick={() => {
                      handleChange({
                        target: {
                          name: "absorbFees"
                        }
                      });
                    }}
                    startIcon={!pricing.absorbFees && <BCPCircleCheck />}
                  >
                    {t("player")}
                  </Button>
                </ButtonGroup>
                <FormHelperText>{t("feeDescription")}</FormHelperText>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary",
              textAlign: "center",
              maxWidth: ["100%", "100%", "calc(50% - 16px)"] // deal with spacing bug
            }}
          >
            <Typography variant="h4" gutterBottom>
              {t("estimatedPayout")}
            </Typography>
            <Typography variant="h1" gutterBottom>
              {pricing.total} {pricing.total !== "-" ? currency : ""}
            </Typography>
            <Typography variant="h4" gutterBottom>
              {t("attendeePays")}
            </Typography>
            <Typography variant="h1" gutterBottom>
              {pricing.amount} {pricing.amount !== "-" ? currency : ""}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              p: 2
            }}
          >
            <Typography variant="caption" paragraph sx={{ mb: 0 }}>
              {t("currencyDisclaimer")}{" "}
              <Link
                href="mailto:support@bestcoastpairings.com"
                underline="none"
              >
                {t("bestCoastPairings")}
              </Link>
              .
            </Typography>
            {feeStructure && (
              <Typography variant="caption" paragraph sx={{ mb: 0 }}>
                {feeStructure.Currency}: {t("feeStructurePrefix")}{" "}
                {!pricing.absorbFees && feeStructure.PercentPlayer !== undefined
                  ? feeStructure.PercentPlayer
                  : feeStructure.Percent}
                % +{" "}
                {(!pricing.absorbFees && feeStructure.FixedPlayer !== undefined
                  ? feeStructure.FixedPlayer
                  : feeStructure.Fixed
                ).toFixed(2)}{" "}
                {t("feeStructureSuffix")}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
